import { useEffect } from 'react';
import { useWorkflowsEditorRouting } from '../context/workflows-editor-routing-context';

/**
 * Hook to warn users when they try to navigate away with unsaved changes.
 * Browser navigation (refresh, close, external links) is guarded here; internal
 * router navigation is guarded through the host-provided registerNavigationGuard
 * (the editor core is router-agnostic).
 */
export function useUnsavedChanges(isDirty: boolean, message?: string) {
  const { registerNavigationGuard } = useWorkflowsEditorRouting();
  const warningMessage = message || 'You have unsaved changes. Are you sure you want to leave?';

  useEffect(() => {
    if (!isDirty) return;

    // Handle browser navigation (refresh, close, external navigation)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    // Handle internal navigation through the host's router
    const unsubscribe = registerNavigationGuard?.(() => warningMessage);

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      unsubscribe?.();
    };
  }, [isDirty, registerNavigationGuard, warningMessage]);
}
